import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  Divider,
  MenuItem,
  IconButton,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useSnackbar } from 'notistack';

import { useAutoSave } from '@hooks/useAutoSave';

import {
  useLazyGetAdminOrganizationArtifactDownloadUrlQuery,
  useLazyGetAdminOrganizationDocumentReferenceQuery,
  useLazyGetArtifactDownloadUrlQuery,
  useLazyGetDocumentReferenceQuery,
} from '@features/patient-artifacts/store/api';

import type { IntakeItem, IntakeSourceRef } from '../types';
import {
  useUpdateAdminIntakeHubItemEvidenceMutation,
  useUpdateIntakeHubItemEvidenceMutation,
} from '../store/api';
import type { IntakeItemStatus } from '../types';

import { IntakeStatusChip } from './IntakeStatusChip';

export type IntakeDocumentAccess =
  | { scope: 'provider' }
  | { scope: 'admin'; organizationId: string; userId?: string | null };

interface IntakeItemDetailsDialogProps {
  open: boolean;
  item: IntakeItem;
  documentAccess?: IntakeDocumentAccess;
  onClose: () => void;
}

type ReferenceDraft = Record<string, string>;
type InformationAutosaveSnapshot = {
  fields: Record<string, string>;
  references: ReferenceDraft[];
  promoteStatus: boolean;
};

const REFERENCE_GROUP_KEY = 'professional_references';
const DEFAULT_REFERENCE_MINIMUM = 3;

function humanizeKey(key: string): string {
  const acronyms = new Set([
    'api',
    'caqh',
    'dea',
    'dob',
    'ehr',
    'ein',
    'id',
    'npi',
    'rcm',
    'ssn',
    'tin',
    'url',
  ]);

  return key
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .map(word => {
      const normalized = word.toLowerCase();
      if (acronyms.has(normalized)) return normalized.toUpperCase();
      return normalized.charAt(0).toUpperCase() + normalized.slice(1);
    })
    .join(' ');
}

function formatDateTime(value?: string | null): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function isPresentValue(value: unknown): boolean {
  if (value == null) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') return Object.keys(value).length > 0;
  return true;
}

function stringArrayValue(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
    : [];
}

function stringifyValue(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) return value.map(stringifyValue).join(', ');
  return JSON.stringify(value);
}

function extractedFieldEntries(item: IntakeItem): Array<[string, unknown]> {
  const fields = item.evidenceSummary.extracted_fields;
  if (!fields || typeof fields !== 'object' || Array.isArray(fields)) return [];
  return Object.entries(fields as Record<string, unknown>).filter(([, value]) =>
    isPresentValue(value),
  );
}

function extractedFieldObject(item: IntakeItem): Record<string, string> {
  const extracted = Object.fromEntries(
    extractedFieldEntries(item).map(([key, value]) => [key, stringifyValue(value)]),
  );
  const expectedFields = item.expectedFields ?? [];
  const singleValueFallback =
    expectedFields.length === 1 ? item.evidenceSummary.value_preview : null;
  for (const field of expectedFields) {
    if (!(field in extracted)) {
      extracted[field] = singleValueFallback ? String(singleValueFallback) : '';
    }
  }
  return extracted;
}

function numberValue(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function recordValue(value: unknown): Record<string, unknown> | null {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function emptyReferenceDraft(fields: string[]): ReferenceDraft {
  return Object.fromEntries(fields.map(field => [field, '']));
}

function referenceDraftFromValue(value: unknown, fields: string[]): ReferenceDraft {
  const record = recordValue(value);
  const draft = emptyReferenceDraft(fields);
  if (!record) return draft;
  for (const field of fields) {
    const fieldValue = record[field];
    draft[field] = fieldValue == null ? '' : stringifyValue(fieldValue);
  }
  return draft;
}

function hasReferenceDraftValue(draft: ReferenceDraft): boolean {
  return Object.values(draft).some(value => value.trim().length > 0);
}

function ensureMinimumReferenceDrafts(
  drafts: ReferenceDraft[],
  fields: string[],
  minimumCount: number,
): ReferenceDraft[] {
  const nextDrafts = drafts.length ? [...drafts] : [];
  while (nextDrafts.length < minimumCount) {
    nextDrafts.push(emptyReferenceDraft(fields));
  }
  return nextDrafts;
}

function referenceDraftsFromItem(
  item: IntakeItem,
  fields: string[],
  minimumCount: number,
): ReferenceDraft[] {
  const extractedFields = recordValue(item.evidenceSummary.extracted_fields) ?? {};
  const references = extractedFields[REFERENCE_GROUP_KEY];
  if (Array.isArray(references)) {
    return ensureMinimumReferenceDrafts(
      references.map(reference => referenceDraftFromValue(reference, fields)),
      fields,
      minimumCount,
    );
  }

  const flatDraft = referenceDraftFromValue(extractedFields, fields);
  return ensureMinimumReferenceDrafts(
    hasReferenceDraftValue(flatDraft) ? [flatDraft] : [],
    fields,
    minimumCount,
  );
}

function sanitizedReferenceDrafts(drafts: ReferenceDraft[]): ReferenceDraft[] {
  return drafts
    .map(draft =>
      Object.fromEntries(
        Object.entries(draft)
          .map(([key, value]) => [key, value.trim()])
          .filter(([, value]) => value.length > 0),
      ),
    )
    .filter(draft => Object.keys(draft).length > 0);
}

function firstPresentNonSensitiveDraftValue(
  fields: Record<string, string>,
  sensitiveFields: Set<string>,
): string | null {
  const firstValue = Object.entries(fields).find(
    ([key, value]) => !sensitiveFields.has(key) && value.trim().length > 0,
  )?.[1];
  return firstValue?.trim() ?? null;
}

function sourceTitle(sourceRef: IntakeSourceRef): string {
  return sourceRef.label || humanizeKey(sourceRef.path || sourceRef.relationship) || 'Document';
}

function latestObservedSourceRef(sourceRefs: IntakeSourceRef[]): IntakeSourceRef {
  return sourceRefs.reduce((latest, sourceRef) => {
    if (!latest.observedAt) return sourceRef;
    if (!sourceRef.observedAt) return latest;
    return new Date(sourceRef.observedAt).getTime() > new Date(latest.observedAt).getTime()
      ? sourceRef
      : latest;
  }, sourceRefs[0]);
}

function displayOtherSourceRefs(sourceRefs: IntakeSourceRef[]): IntakeSourceRef[] {
  const manualReplacementRefs = sourceRefs.filter(
    sourceRef => sourceRef.type === 'manual_entry' && sourceRef.relationship === 'replacement',
  );
  if (manualReplacementRefs.length <= 1) return sourceRefs;

  return [
    latestObservedSourceRef(manualReplacementRefs),
    ...sourceRefs.filter(
      sourceRef => !(sourceRef.type === 'manual_entry' && sourceRef.relationship === 'replacement'),
    ),
  ];
}

export function IntakeItemDetailsDialog({
  open,
  item,
  documentAccess = { scope: 'provider' },
  onClose,
}: IntakeItemDetailsDialogProps) {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [isEditing, setIsEditing] = useState(false);
  const [labelDraft, setLabelDraft] = useState('');
  const [valueDraft, setValueDraft] = useState('');
  const [statusDraft, setStatusDraft] = useState<IntakeItemStatus>(item.status);
  const [fieldDrafts, setFieldDrafts] = useState<Record<string, string>>({});
  const [referenceDrafts, setReferenceDrafts] = useState<ReferenceDraft[]>([]);
  const autosaveHydratedRef = useRef(false);
  const autosaveSignatureRef = useRef('');
  const hydratedDialogKeyRef = useRef('');
  const [fetchProviderDocumentReference] = useLazyGetDocumentReferenceQuery();
  const [fetchProviderDownloadUrl] = useLazyGetArtifactDownloadUrlQuery();
  const [fetchAdminDocumentReference] = useLazyGetAdminOrganizationDocumentReferenceQuery();
  const [fetchAdminDownloadUrl] = useLazyGetAdminOrganizationArtifactDownloadUrlQuery();
  const [updateProviderEvidence, { isLoading: isSavingProviderEvidence }] =
    useUpdateIntakeHubItemEvidenceMutation();
  const [updateAdminEvidence, { isLoading: isSavingAdminEvidence }] =
    useUpdateAdminIntakeHubItemEvidenceMutation();

  const fields = useMemo(() => extractedFieldEntries(item), [item]);
  const documentSources = item.sourceRefs.filter(
    sourceRef => sourceRef.type === 'document_reference',
  );
  const otherSources = item.sourceRefs.filter(
    sourceRef => sourceRef.type !== 'document_reference',
  );
  const visibleOtherSources = useMemo(() => displayOtherSourceRefs(otherSources), [otherSources]);
  const isInformationItem = item.type === 'information';
  const isReferenceItem =
    item.conceptKey === REFERENCE_GROUP_KEY || item.metadata.per_reference === true;
  const referenceFields = useMemo(
    () =>
      item.expectedFields?.length
        ? item.expectedFields
        : [
            'reference_name',
            'reference_credentials',
            'reference_email',
            'reference_phone',
            'relationship',
            'years_known',
          ],
    [item.expectedFields],
  );
  const referenceMinimumCount = numberValue(item.metadata.min_count, DEFAULT_REFERENCE_MINIMUM);
  const completedReferenceCount = sanitizedReferenceDrafts(referenceDrafts).length;
  const sensitiveFields = useMemo(
    () => new Set(stringArrayValue(item.metadata.sensitive_fields)),
    [item.metadata.sensitive_fields],
  );
  const hasSources = documentSources.length > 0 || visibleOtherSources.length > 0;
  const summaryValue =
    item.evidenceSummary.label ?? item.evidenceSummary.value_preview ?? item.updatedAtLabel;
  const isSavingEvidence = isSavingProviderEvidence || isSavingAdminEvidence;

  useEffect(() => {
    if (!open) {
      hydratedDialogKeyRef.current = '';
      return;
    }

    const dialogKey = item.intakeItemId ?? item.id;
    if (hydratedDialogKeyRef.current === dialogKey) return;
    hydratedDialogKeyRef.current = dialogKey;

    setIsEditing(false);
    setLabelDraft(String(item.evidenceSummary.label ?? ''));
    setValueDraft(String(item.evidenceSummary.value_preview ?? ''));
    setStatusDraft(item.status);
    const nextFieldDrafts = extractedFieldObject(item);
    const nextReferenceDrafts = referenceDraftsFromItem(item, referenceFields, referenceMinimumCount);
    setFieldDrafts(nextFieldDrafts);
    setReferenceDrafts(nextReferenceDrafts);
    autosaveHydratedRef.current = false;
    autosaveSignatureRef.current = JSON.stringify({
      fields: nextFieldDrafts,
      references: nextReferenceDrafts,
    });
  }, [item.id, item.intakeItemId, open]);

  const handleOpenDocument = useCallback(
    async (sourceRef: IntakeSourceRef) => {
      try {
        const documentReference =
          documentAccess.scope === 'admin'
            ? await fetchAdminDocumentReference({
                organizationId: documentAccess.organizationId,
                documentReferenceId: sourceRef.id,
              }).unwrap()
            : await fetchProviderDocumentReference({
                documentReferenceId: sourceRef.id,
              }).unwrap();

        const artifactId = documentReference.source_artifact_id;
        if (!artifactId) {
          enqueueSnackbar('This document does not have an original upload attached yet.', {
            variant: 'info',
          });
          return;
        }

        const download =
          documentAccess.scope === 'admin'
            ? await fetchAdminDownloadUrl({
                organizationId: documentAccess.organizationId,
                artifactId,
              }).unwrap()
            : await fetchProviderDownloadUrl({ artifactId }).unwrap();

        window.open(download.url, '_blank', 'noopener,noreferrer');
      } catch (error) {
        console.error('Failed to open intake document source', error);
        enqueueSnackbar('Could not open that document yet.', { variant: 'error' });
      }
    },
    [
      documentAccess,
      enqueueSnackbar,
      fetchAdminDocumentReference,
      fetchAdminDownloadUrl,
      fetchProviderDocumentReference,
      fetchProviderDownloadUrl,
    ],
  );

  const handleSaveEvidence = useCallback(async () => {
    const intakeItemId = item.intakeItemId ?? item.id;
    const extractedFields = Object.fromEntries(
      Object.entries(fieldDrafts)
        .map(([key, value]) => [key, value.trim()])
        .filter(([, value]) => value.length > 0),
    );
    const request = {
      label: labelDraft.trim() || null,
      value_preview: valueDraft.trim() || null,
      extracted_fields: extractedFields,
      status: documentAccess.scope === 'admin' ? statusDraft : 'submitted',
      needs_review: documentAccess.scope !== 'admin' || statusDraft !== 'accepted',
    };

    try {
      if (documentAccess.scope === 'admin') {
        await updateAdminEvidence({
          organizationId: documentAccess.organizationId,
          userId: documentAccess.userId,
          intakeItemId,
          updateItemEvidenceRequest: request,
        }).unwrap();
      } else {
        await updateProviderEvidence({
          intakeItemId,
          updateItemEvidenceRequest: request,
        }).unwrap();
      }
      enqueueSnackbar('Information updated', { variant: 'success' });
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to update intake item evidence', error);
      enqueueSnackbar('Could not update this information.', { variant: 'error' });
    }
  }, [
    documentAccess,
    enqueueSnackbar,
    fieldDrafts,
    item.id,
    item.intakeItemId,
    labelDraft,
    statusDraft,
    updateAdminEvidence,
    updateProviderEvidence,
    valueDraft,
  ]);

  const saveInformationEvidence = useCallback(
    async ({ fields: drafts, references, promoteStatus }: InformationAutosaveSnapshot) => {
      const intakeItemId = item.intakeItemId ?? item.id;
      const cleanReferences = isReferenceItem ? sanitizedReferenceDrafts(references) : [];
      const extractedFields = isReferenceItem
        ? cleanReferences.length > 0
          ? { [REFERENCE_GROUP_KEY]: cleanReferences }
          : {}
        : Object.fromEntries(
            Object.entries(drafts)
              .map(([key, value]) => [key, value.trim()])
              .filter(([, value]) => value.length > 0),
          );
      const hasValue = isReferenceItem
        ? cleanReferences.length >= referenceMinimumCount
        : Object.keys(extractedFields).length > 0;
      const completedStatus: IntakeItemStatus =
        isReferenceItem && !hasValue ? 'missing' : hasValue ? 'submitted' : item.status;
      const status: IntakeItemStatus =
        item.status === 'accepted' ? 'accepted' : promoteStatus ? completedStatus : item.status;
      const request = {
        label: item.label,
        value_preview: isReferenceItem
          ? cleanReferences.length > 0
            ? `${cleanReferences.length} professional reference${
                cleanReferences.length === 1 ? '' : 's'
              }`
            : null
          : (firstPresentNonSensitiveDraftValue(drafts, sensitiveFields) ??
            (Object.keys(extractedFields).length > 0 ? `${item.label} added` : null)),
        extracted_fields: extractedFields,
        status,
        needs_review: status !== 'accepted',
      };

      try {
        if (documentAccess.scope === 'admin') {
          await updateAdminEvidence({
            organizationId: documentAccess.organizationId,
            userId: documentAccess.userId,
            intakeItemId,
            updateItemEvidenceRequest: request,
          }).unwrap();
        } else {
          await updateProviderEvidence({
            intakeItemId,
            updateItemEvidenceRequest: request,
          }).unwrap();
        }
      } catch (error) {
        console.error('Failed to update intake item information', error);
        enqueueSnackbar('Could not save this information.', { variant: 'error' });
        throw error;
      }
    },
    [
      documentAccess,
      enqueueSnackbar,
      item.id,
      item.intakeItemId,
      item.label,
      item.status,
      isReferenceItem,
      referenceMinimumCount,
      sensitiveFields,
      updateAdminEvidence,
      updateProviderEvidence,
    ],
  );

  const {
    triggerAutoSave: triggerInformationAutosave,
    saveImmediately: saveInformationImmediately,
    status: autosaveState,
  } = useAutoSave<InformationAutosaveSnapshot>({
    onSave: saveInformationEvidence,
    debounceMs: 650,
    enabled: open && isInformationItem,
    flushOnUnmount: true,
  });

  useEffect(() => {
    if (!open || !isInformationItem) return undefined;

    const signature = JSON.stringify({
      fields: fieldDrafts,
      references: isReferenceItem ? referenceDrafts : [],
    });
    if (!autosaveHydratedRef.current) {
      autosaveHydratedRef.current = true;
      autosaveSignatureRef.current = signature;
      return undefined;
    }
    if (signature === autosaveSignatureRef.current) return undefined;

    autosaveSignatureRef.current = signature;
    triggerInformationAutosave({
      fields: fieldDrafts,
      references: referenceDrafts,
      promoteStatus: false,
    });
    return undefined;
  }, [
    fieldDrafts,
    isInformationItem,
    isReferenceItem,
    open,
    referenceDrafts,
    triggerInformationAutosave,
  ]);

  const handleClose = useCallback(() => {
    if (isInformationItem) {
      const signature = JSON.stringify({
        fields: fieldDrafts,
        references: isReferenceItem ? referenceDrafts : [],
      });
      autosaveSignatureRef.current = signature;
      saveInformationImmediately({
        fields: fieldDrafts,
        references: referenceDrafts,
        promoteStatus: true,
      });
    }
    onClose();
  }, [
    fieldDrafts,
    isInformationItem,
    isReferenceItem,
    onClose,
    referenceDrafts,
    saveInformationImmediately,
  ]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: 2.5,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <Stack
          direction="row"
          spacing={1.5}
          sx={{
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            px: { xs: 2, sm: 2.5 },
            py: 2,
            borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 900 }}>
              {isInformationItem ? 'Information' : 'Evidence'}
            </Typography>
            <Typography variant="h6" sx={{ mt: 0.25, fontSize: 20 }}>
              {item.label}
            </Typography>
            {item.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {item.description}
              </Typography>
            )}
          </Box>
          <IconButton size="small" aria-label="Close details" onClick={handleClose}>
            <CloseOutlinedIcon fontSize="small" />
          </IconButton>
        </Stack>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: hasSources ? 'minmax(0, 1fr) 300px' : 'minmax(0, 1fr)',
            },
            minHeight: { md: 460 },
          }}
        >
          <Stack spacing={2.25} sx={{ p: { xs: 2, sm: 2.5 }, minWidth: 0 }}>
            <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
              <IntakeStatusChip status={item.status} />
              <Chip size="small" variant="outlined" label={humanizeKey(item.owner)} />
              <Chip size="small" variant="outlined" label={humanizeKey(item.type)} />
            </Stack>

            {summaryValue && !isInformationItem && (
              <Box>
                <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 900 }}>
                  Current value
                </Typography>
                <Typography variant="body1" sx={{ mt: 0.35, fontWeight: 750 }}>
                  {String(summaryValue)}
                </Typography>
              </Box>
            )}

            {isInformationItem ? (
              <Box>
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 1 }}
                >
                  <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 900 }}>
                    Information
                  </Typography>
                  {autosaveState !== 'idle' && (
                    <Typography
                      variant="caption"
                      color={autosaveState === 'error' ? 'error.main' : 'text.secondary'}
                      sx={{ fontWeight: 750 }}
                    >
                      {autosaveState === 'saving'
                        ? 'Saving...'
                        : autosaveState === 'saved'
                          ? 'Saved'
                          : 'Could not save'}
                    </Typography>
                  )}
                </Stack>
                {isReferenceItem ? (
                  <Stack spacing={1.25}>
                    <Typography variant="body2" color="text.secondary">
                      {completedReferenceCount} of {referenceMinimumCount} required references
                      added.
                    </Typography>
                    {referenceDrafts.map((referenceDraft, referenceIndex) => (
                      <Box
                        key={referenceIndex}
                        sx={{
                          p: 1.5,
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                          borderRadius: 1.5,
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 1 }}
                        >
                          <Typography variant="subtitle2" sx={{ fontWeight: 850 }}>
                            Reference {referenceIndex + 1}
                          </Typography>
                          {referenceDrafts.length > referenceMinimumCount && (
                            <Button
                              size="small"
                              color="error"
                              startIcon={<DeleteOutlineOutlinedIcon fontSize="small" />}
                              onClick={() =>
                                setReferenceDrafts(current =>
                                  current.filter((_, index) => index !== referenceIndex),
                                )
                              }
                              sx={{ textTransform: 'none' }}
                            >
                              Remove
                            </Button>
                          )}
                        </Stack>
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                              xs: '1fr',
                              sm: 'repeat(2, minmax(0, 1fr))',
                            },
                            gap: 1.25,
                          }}
                        >
                          {referenceFields.map(field => (
                            <TextField
                              key={`${referenceIndex}-${field}`}
                              label={humanizeKey(field)}
                              size="small"
                              value={referenceDraft[field] ?? ''}
                              onChange={event => {
                                const nextValue = event.target.value;
                                setReferenceDrafts(current =>
                                  current.map((draft, index) =>
                                    index === referenceIndex
                                      ? { ...draft, [field]: nextValue }
                                      : draft,
                                  ),
                                );
                              }}
                              fullWidth
                            />
                          ))}
                        </Box>
                      </Box>
                    ))}
                    <Button
                      variant="outlined"
                      startIcon={<AddOutlinedIcon fontSize="small" />}
                      onClick={() =>
                        setReferenceDrafts(current => [
                          ...current,
                          emptyReferenceDraft(referenceFields),
                        ])
                      }
                      sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
                    >
                      Add reference
                    </Button>
                  </Stack>
                ) : (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                      gap: 1.25,
                    }}
                  >
                    {Object.entries(fieldDrafts).map(([key, value]) => (
                      <TextField
                        key={key}
                        label={humanizeKey(key)}
                        size="small"
                        type={sensitiveFields.has(key) ? 'password' : 'text'}
                        autoComplete={sensitiveFields.has(key) ? 'new-password' : undefined}
                        value={value}
                        onChange={event =>
                          setFieldDrafts(current => ({
                            ...current,
                            [key]: event.target.value,
                          }))
                        }
                        fullWidth
                      />
                    ))}
                    {Object.keys(fieldDrafts).length === 0 && (
                      <TextField
                        label={item.label}
                        size="small"
                        type={sensitiveFields.has('value') ? 'password' : 'text'}
                        autoComplete={sensitiveFields.has('value') ? 'new-password' : undefined}
                        value={valueDraft}
                        onChange={event => {
                          const nextValue = event.target.value;
                          setValueDraft(nextValue);
                          setFieldDrafts({ value: nextValue });
                        }}
                        fullWidth
                      />
                    )}
                  </Box>
                )}
              </Box>
            ) : (
              <Box>
              <Stack
                direction="row"
                spacing={1}
                sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 1 }}
              >
                <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 900 }}>
                  Details
                </Typography>
                {isEditing ? (
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      variant="text"
                      disabled={isSavingEvidence}
                      onClick={() => setIsEditing(false)}
                      sx={{ textTransform: 'none' }}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      disabled={isSavingEvidence}
                      onClick={() => void handleSaveEvidence()}
                      sx={{ textTransform: 'none' }}
                    >
                      Save
                    </Button>
                  </Stack>
                ) : (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<EditOutlinedIcon fontSize="small" />}
                    onClick={() => setIsEditing(true)}
                    sx={{ textTransform: 'none' }}
                  >
                    Edit
                  </Button>
                )}
              </Stack>

              {isEditing ? (
                <Stack spacing={1.25}>
                  <TextField
                    label="Label"
                    size="small"
                    value={labelDraft}
                    onChange={event => setLabelDraft(event.target.value)}
                    fullWidth
                  />
                  <TextField
                    label="Display value"
                    size="small"
                    value={valueDraft}
                    onChange={event => setValueDraft(event.target.value)}
                    fullWidth
                  />
                  {documentAccess.scope === 'admin' && (
                    <Select
                      size="small"
                      value={statusDraft}
                      onChange={event => setStatusDraft(event.target.value as IntakeItemStatus)}
                      fullWidth
                    >
                      {[
                        'submitted',
                        'in_review',
                        'accepted',
                        'needs_attention',
                        'ai_flagged',
                        'not_applicable',
                      ].map(status => (
                        <MenuItem key={status} value={status}>
                          {humanizeKey(status)}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                  {Object.entries(fieldDrafts).map(([key, value]) => (
                      <TextField
                        key={key}
                        label={humanizeKey(key)}
                        size="small"
                        type={sensitiveFields.has(key) ? 'password' : 'text'}
                        autoComplete={sensitiveFields.has(key) ? 'new-password' : undefined}
                        value={value}
                      onChange={event =>
                        setFieldDrafts(current => ({
                          ...current,
                          [key]: event.target.value,
                        }))
                      }
                      fullWidth
                    />
                  ))}
                </Stack>
              ) : fields.length > 0 ? (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                    gap: 1,
                  }}
                >
                  {fields.map(([key, value]) => (
                    <Box
                      key={key}
                      sx={{
                        p: 1.25,
                        borderRadius: 1.5,
                        bgcolor: alpha(theme.palette.grey[500], 0.07),
                        minWidth: 0,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
                        {humanizeKey(key)}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.25, wordBreak: 'break-word' }}>
                        {stringifyValue(value)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No details have been added yet.
                </Typography>
              )}
              </Box>
            )}
          </Stack>

          {hasSources && (
            <Stack
              spacing={1.5}
              sx={{
                p: { xs: 2, sm: 2.5 },
                borderTop: { xs: `1px solid ${theme.palette.divider}`, md: 0 },
                borderLeft: { md: `1px solid ${theme.palette.divider}` },
                bgcolor: alpha(theme.palette.grey[500], 0.035),
                minWidth: 0,
              }}
            >
              {documentSources.length > 0 && (
                <Box>
                  <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 900 }}>
                    Documents
                  </Typography>
                  <Stack spacing={1} sx={{ mt: 1 }}>
                    {documentSources.map(sourceRef => (
                      <Box
                        key={`${sourceRef.type}-${sourceRef.id}-${sourceRef.relationship}`}
                        sx={{
                          p: 1.25,
                          borderRadius: 1.5,
                          bgcolor: 'background.paper',
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                        }}
                      >
                        <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
                          <DescriptionOutlinedIcon
                            sx={{ mt: 0.2, fontSize: 20, color: 'text.secondary' }}
                          />
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 800 }} noWrap>
                              {sourceTitle(sourceRef)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {humanizeKey(sourceRef.relationship)}
                              {sourceRef.observedAt
                                ? ` · ${formatDateTime(sourceRef.observedAt)}`
                                : ''}
                            </Typography>
                          </Box>
                        </Stack>
                        <Button
                          size="small"
                          fullWidth
                          variant="text"
                          endIcon={<OpenInNewOutlinedIcon fontSize="small" />}
                          onClick={() => void handleOpenDocument(sourceRef)}
                          sx={{ mt: 1, justifyContent: 'space-between', textTransform: 'none' }}
                        >
                          Open document
                        </Button>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}

              {visibleOtherSources.length > 0 && (
                <>
                  {documentSources.length > 0 && <Divider />}
                  <Box>
                    <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 900 }}>
                      Other sources
                    </Typography>
                    <Stack spacing={0.75} sx={{ mt: 1 }}>
                      {visibleOtherSources.map(sourceRef => (
                        <Box
                          key={`${sourceRef.type}-${sourceRef.id}-${sourceRef.relationship}`}
                          sx={{
                            p: 1,
                            borderRadius: 1.25,
                            bgcolor: alpha(theme.palette.grey[500], 0.08),
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 750 }}>
                            {sourceTitle(sourceRef)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {humanizeKey(sourceRef.type)} · {humanizeKey(sourceRef.relationship)}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                </>
              )}
            </Stack>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
